import { useEffect, useState } from "react";
import { Robot, ClockCounterClockwise } from "@phosphor-icons/react";
import { fetchAiProvenance } from "@/lib/api";
import { useT } from "@/lib/i18n";

const TOOL_KEY = {
  create_customer:    "ai.provenance.create_customer",
  update_customer:    "ai.provenance.update_customer",
  create_person:      "ai.provenance.create_person",
  update_person:      "ai.provenance.update_person",
  create_quote_draft: "ai.provenance.create_quote_draft",
  add_quote_line:     "ai.provenance.add_quote_line",
  send_quote_email:   "ai.provenance.send_quote_email",
};
const TOOL_FALLBACK = {
  create_customer:    "Created by AI",
  update_customer:    "Edited by AI",
  create_person:      "Contact added by AI",
  update_person:      "Contact edited by AI",
  create_quote_draft: "Drafted by AI",
  add_quote_line:     "Line added by AI",
  send_quote_email:   "Emailed via AI",
};

/** Small inline badge that surfaces the most recent AI-executed action that
 *  touched ``{entityType}/{entityId}``. Renders nothing while loading or if
 *  the entity has no AI history. Hovering shows the full provenance list. */
export default function AIProvenanceBadge({ entityType, entityId, userEmail }) {
  const t = useT();
  const [items, setItems] = useState(null);   // null = loading, [] = none, [...] = found
  const [open, setOpen] = useState(false);

  const labelFor = (toolName) => {
    const key = TOOL_KEY[toolName];
    const fb = TOOL_FALLBACK[toolName] || t("ai.provenance.fallback", "AI action");
    return key ? t(key, fb) : fb;
  };

  useEffect(() => {
    let cancelled = false;
    if (!entityType || !entityId || !userEmail) {
      setItems([]);
      return;
    }
    fetchAiProvenance(entityType, entityId, userEmail)
      .then(d => { if (!cancelled) setItems(d.items || []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [entityType, entityId, userEmail]);

  if (items === null || items.length === 0) return null;
  const newest = items[0];
  const label = labelFor(newest.tool_name);
  const when = newest.ts ? new Date(newest.ts).toLocaleString() : "";
  return (
    <span className="relative inline-flex items-center" data-testid={`ai-provenance-${entityType}-${entityId}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-purple-100 text-purple-800 hover:bg-purple-200"
        title={`${label} · ${when}`}
        data-testid="ai-provenance-trigger">
        <Robot size={11} weight="fill" /> {label}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-30 w-72 bg-white border border-slate-200 rounded-sm shadow-lg p-2 text-[11px]"
             data-testid="ai-provenance-popover">
          <div className="font-bold text-slate-900 mb-1.5 inline-flex items-center gap-1">
            <ClockCounterClockwise size={11} weight="bold" /> {t("ai.provenance.title", "AI provenance")}
          </div>
          <ul className="space-y-1.5">
            {items.slice(0, 5).map((it) => (
              <li key={it.id} className="border-b border-slate-100 pb-1 last:border-0">
                <div className="font-bold text-slate-800">
                  {labelFor(it.tool_name)}
                </div>
                <div className="text-slate-500">
                  {it.ts ? new Date(it.ts).toLocaleString() : ""}
                </div>
                <div className="text-slate-500 truncate">{it.actor_email}</div>
              </li>
            ))}
          </ul>
          {items.length > 5 && (
            <div className="text-slate-400 text-[10px] mt-1">
              {t("ai.provenance.more", "+{n} earlier action(s)").replace("{n}", items.length - 5)}
            </div>
          )}
        </div>
      )}
    </span>
  );
}
